import { lookByNumber, newWireLook, settleWireLook, type Appearance, type CreatorRow, type Looks, type WireLook } from "odyssey-shared";
import { say } from "../../words.ts";
import type { LookOutcome } from "../auth.ts";
import type { LookPreviewSource } from "../look-preview/look-figure.tsx";
import type { LookRefusal } from "../look-preview/look-panel.tsx";

/**
 * The temporary Change look screen (ADR-094's amendment of 2026-10-06 "a temporary Change look screen in the game",
 * B11 to B14; R-0221): what the game menu's item and its window need from the page, the words they show, and the
 * pure steps between the page's held look and the panel. The screen is temporary: clothes become equipment and a
 * proper appearance editor replaces it, at which point `TEMPORARY_LOOK_SCREEN` goes false and the item with it.
 */

/**
 * Whether the page offers the temporary screen: the one switch that removes the menu's item and leaves the route,
 * the panel and the rest in place (B14).
 */
export const TEMPORARY_LOOK_SCREEN = true;

/**
 * How long Confirm rests after an accepted change, in milliseconds: a courtesy, no part of the gateway's limit (B5).
 */
export const LOOK_CONFIRM_REST_MS = 2000;

/**
 * What the screen needs from the page. `change` never rejects, and for a session that is gone it has already taken
 * the page out of the world, as a refused refresh does, before it answers `lost`.
 */
export interface LookScreenPage {
  /** The page's looks table. */
  readonly looks: Looks;
  /** The character's name, for the window's title. */
  readonly name: () => string;
  /** The look the page's own character holds, as keys; null when none has arrived. */
  readonly current: () => Appearance | null;
  /**
   * The character's voice as the page last knew it stored (ADR-094 note 126): the one it joined with until a change
   * is accepted, then that one; null for none.
   */
  readonly voice: () => string | null;
  /**
   * Plays a sample of a voice.
   *
   * @param voice The voice, null for none.
   * @returns False when it has no sample to play.
   */
  readonly hear: (voice: string | null) => boolean;
  /** What the preview draws from, or null when the page's look sheets did not load. */
  readonly source: () => LookPreviewSource | null;
  /**
   * Asks the gateway to change the character's look and voice, together (ADR-094 note 126).
   *
   * @param look The three keys.
   * @param voice The voice, null for none.
   * @returns Accepted, or why not.
   */
  readonly change: (look: Appearance, voice: string | null) => Promise<LookOutcome>;
  /** Whether the page's freeze notice stands, which disables the menu's item. */
  readonly frozen: () => boolean;
  /**
   * Tells the menu when the freeze notice may have come or gone.
   *
   * @param listener Called on each change of the page's notices.
   * @returns A function that removes it.
   */
  readonly watchFrozen: (listener: () => void) => () => void;
  /** Whether the pointer is coarse, so swatches and turn buttons are the touch size. */
  readonly touch?: boolean;
  /** The page's clock in milliseconds; `Date.now` unless given. */
  readonly now?: () => number;
}

/**
 * A look the page holds for a character, back as the three keys a player chose: the skin's, the hair's and the
 * outfit's pieces by their numbers in the page's table.
 *
 * @param looks The page's looks table.
 * @param look The look as it arrived, or undefined when none has.
 * @returns The keys, an empty key for a slot whose number names no piece of the table (which `creatorStart` then
 *   replaces); null when there is no look.
 * @example
 * appearanceOfLook(looks, { skin: 3, hair: 59, flags: 0, outfit: 49, hat: 0, mainHand: 57, offHand: 58 });
 * // { skin: "humn-3", hair: "none-1", clothes: "fstr-3" }
 */
export function appearanceOfLook(looks: Looks, look: Readonly<WireLook> | undefined): Appearance | null {
  if (look === undefined) {
    return null;
  }
  const settled = newWireLook();
  settleWireLook(looks, look, settled);
  return { skin: lookByNumber(looks, settled.skin)?.key ?? "", hair: lookByNumber(looks, settled.hair)?.key ?? "", clothes: lookByNumber(looks, settled.outfit)?.key ?? "" };
}

/**
 * Whether two looks are the same three keys.
 *
 * @param a One.
 * @param b The other.
 * @returns True when no key differs.
 */
export function sameAppearance(a: Appearance, b: Appearance): boolean {
  return a.skin === b.skin && a.hair === b.hair && a.clothes === b.clothes;
}

/**
 * The row a refused field is shown under.
 */
const FIELD_ROWS: Readonly<Record<"skin" | "hair" | "clothes", CreatorRow>> = { skin: "skin", hair: "hairStyle", clothes: "clothesStyle" };

/**
 * What the panel shows for a change that was not accepted: the gateway's sentence for a refused look, under the row
 * of the field it named, and for a frozen account; the page's own for the limit and for no usable answer.
 *
 * @param outcome Why the change was not accepted; never `lost`, which shows nothing.
 * @returns The sentence and its row.
 */
export function lookRefusal(outcome: Extract<LookOutcome, { readonly ok: false; readonly message: string }>): LookRefusal {
  switch (outcome.why) {
    case "look":
      return { sentence: outcome.message, row: outcome.field === null ? null : FIELD_ROWS[outcome.field] };
    case "voice":
      return { sentence: outcome.message, row: null };
    case "frozen":
      return { sentence: outcome.message, row: null };
    case "rate":
      return { sentence: say("look.change.rate"), row: null };
    case "unanswered":
      return { sentence: say("look.change.unanswered"), row: null };
  }
}
